import { styled } from '@linaria/react';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAttribute, formatMeters, formatSectionLabel } from '../../../../core/mapV2/format';
import { singleSelectedSectionQuery } from '../../../../core/mapV2/queries';
import { MULTI_ATTRIBUTE_FIELDS } from '../../../../core/mapV2/selection';
import { editDialogOpened } from '../../../../core/mapV2/state';
import theme from '../../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../../types';
import { Group, GroupTitle, Rows, Sheet } from './panelStyles';

const Badge = styled.span`
    display: inline-block;
    margin-bottom: 8px;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.color.grey.dark};
    background-color: ${theme.color.grey.medium};
`;

const Identifier = styled.h2`
    margin: 0 0 16px;
    font-size: 16px;
    font-weight: 600;
    color: ${theme.color.grey.dark};
`;

// Right-panel content for one selected tronçon: identifier, where it is, and its referential attributes.
// Each attribute has a pencil that opens the edit dialog. Measured characteristics come with the sessions layer.
export const SegmentSheet: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const section = useAppSelector(singleSelectedSectionQuery);

    if (!section) {
        return null;
    }

    return (
        <Sheet data-testid="map-v2-segment-sheet">
            <Badge>{t('mapV2.sheet.badge')}</Badge>
            <Identifier>{formatSectionLabel(section)}</Identifier>

            <Group>
                <GroupTitle>{t('mapV2.sheet.location')}</GroupTitle>
                <Rows>
                    <dt>{t('mapV2.sheet.axis')}</dt>
                    <dd>{section.roadName}</dd>
                    <dt>{t('mapV2.sheet.start')}</dt>
                    <dd>{formatMeters(section.cumulStart)}</dd>
                    <dt>{t('mapV2.sheet.end')}</dt>
                    <dd>{formatMeters(section.cumulEnd)}</dd>
                    <dt>{t('mapV2.sheet.length')}</dt>
                    <dd>{formatMeters(Math.abs(section.cumulEnd - section.cumulStart))}</dd>
                </Rows>
            </Group>

            <Group>
                <GroupTitle>{t('mapV2.sheet.sectionAttributes')}</GroupTitle>
                <Rows>
                    {MULTI_ATTRIBUTE_FIELDS.map((field) => (
                        <Fragment key={field}>
                            <dt>{t(`mapV2.sheet.${field}`)}</dt>
                            <dd style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                <span style={{ flex: 1 }}>{formatAttribute(field, section[field])}</span>
                                <IconButton
                                    size="small"
                                    aria-label={t('mapV2.multi.editField', {
                                        field: t(`mapV2.sheet.${field}`).toLowerCase(),
                                    })}
                                    title={t('mapV2.multi.editField', {
                                        field: t(`mapV2.sheet.${field}`).toLowerCase(),
                                    })}
                                    onClick={() => dispatch(editDialogOpened(field))}
                                >
                                    <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </dd>
                        </Fragment>
                    ))}
                </Rows>
            </Group>
        </Sheet>
    );
};
